import moment from 'moment';
import { StorageManager } from '../../../../../../common/storage-manager.class';
import {
    InputType,
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
} from '@fitogest/reactive-form-builder/interfaces';
import { checkIfRolAdmin } from '../../../../../../common/check-role';
import { TareaPlantacionModel } from '../../../../../../models/tareas/tarea-plantacion.model';
import { FincasModel } from '../../../../../../models/form-common/fincas.module';
import { MantenimientoModel } from '../../../../../../models/trabajadores/mantenimiento.model';
import { VisitasVariedadMesModel } from '../../../../../../models/visitas_variedad_mes/visitas_variedad_mes.model';
import { TareasPlantacionEnvironmentDefinition } from '../../environment-variables';
import { TareasPlantacionListsDefinition } from './list-variables';
import { getDropdownSelectedSingle } from '@fitogest/reactive-form-builder/utils';

const TRUTHY_VALUES = new Set<unknown>([1, '1', true, 'true']);

export class TareasPlantacionFormDefinition {
    public env: TareasPlantacionEnvironmentDefinition = new TareasPlantacionEnvironmentDefinition();
    public lists: TareasPlantacionListsDefinition = new TareasPlantacionListsDefinition();

    public formType!: FormType;
    public model: Partial<TareaPlantacionModel> = {};
    public formFields!: Form<TareaPlantacionModel>;
    public fieldNames: FormField<TareaPlantacionModel>[] = [];
    public fieldLabels: string[] = [];

    private readonly user = StorageManager.getUser();
    private readonly client = StorageManager.getClient();

    constructor(type: FormType, model: Partial<TareaPlantacionModel>) {
        this.formType = +type;
        this.model = {
            ...model,
        };

        this.formFields = {
            name: 'plantacion',
            header: 'Datos de Plantación',
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: true,
            signatureConfiguration: {
                database: 'tareas_plantacion',
                folder: 'plantacion',
                field: 'signature_img',
            },
        };
    }

    public calculateHoras(): string {
        return this.formatHoursMinutes(
            this.normalizeTimeValue(this.model.hora_inicio),
            this.normalizeTimeValue(this.model.hora_fin),
        );
    }

    private formatHoursMinutes(startTime: string, endTime: string): string {
        if (!startTime || !endTime) {
            return '';
        }

        const start = moment(startTime, 'HH:mm', true);
        const end = moment(endTime, 'HH:mm', true);

        if (!start.isValid() || !end.isValid()) {
            console.error('Invalid time format. Expected HH:mm.', {
                startTime,
                endTime,
            });

            return '';
        }

        if (end.isBefore(start)) {
            console.warn('End time is before start time.', {
                startTime,
                endTime,
            });

            return '';
        }

        const duration = moment.duration(end.diff(start));
        const hours = Math.floor(duration.asHours()).toString().padStart(2, '0');
        const minutes = duration.minutes().toString().padStart(2, '0');

        return `${hours}:${minutes}`;
    }


    public updateMantenimientoLabel(): void {
        const field = this.getFieldByName('mantenimiento');

        if (!field) {
            return;
        }

        field.label = this.isTruthy(this.model.mantenimiento) ? 'Creación' : 'Mantenimiento';
    }

    private getFormSections(): FormSection<TareaPlantacionModel>[] {
        const sections: FormSection<TareaPlantacionModel>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getMainFields(),
                order: 1,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getSimpleFields(),
                order: 2,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getWorkerFields(),
                order: 3,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getWorkOrderFields(),
                order: 4,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getObservationFields(),
                order: 5,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 6,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getMainFields(): FormField<TareaPlantacionModel>[] {
        const fields: FormField<TareaPlantacionModel>[] = [
            {
                name: this.formType < 3 ? 'id_cliente' : 'cliente',
                vinculatedFields: undefined,
                label: this.env.clienteName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.clientes,
                    isEditable: true,
                    dropdownConfiguration: { valuePrimaryKey: 'id' },
                },
                width: 3,
                conditions: this.conditions({
                    isRequired: true,
                    isVisible: this.env.canSeeClientes && !this.client && this.env.hasClients,
                }),
                order: 1,
            },
            {
                name: 'fecha',
                vinculatedFields: undefined,
                label: 'Fecha',
                input: {
                    type: InputType.CALENDAR,
                    defaultValue: this.formType === FormType.CREATION ? new Date() : undefined,
                    isEditable: true,
                    calendarConfiguration: {
                        calendarView: 'date',
                        calendarDateFormat: 'dd/mm/yy',
                    },
                },
                width: 3,
                conditions: this.conditions({ isRequired: true }),
                order: 2,
            },
            {
                name: this.formType < 3 ? 'id_finca' : 'finca',
                dependencies: [
                    {
                        source: 'id_cliente',
                        target: this.formType < 3 ? 'id_finca' : 'finca',
                        kind: 'filter'
                    }
                ],
                vinculatedFields: undefined,
                label: this.env.fincaName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.fincas,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it): boolean => this.filterFincaByClient(it.value as FincasModel | null),
                    },
                },
                width: 3,
                conditions: this.conditions({ isRequired: true }),
                order: 3,
            },
            {
                name: 'hora_inicio',
                vinculatedFields: undefined,
                label: 'Hora Inicio',
                input: { type: InputType.TIME, isEditable: true },
                width: 3,
                conditions: this.conditions({
                    isRememberedOnDuplicate: !this.env.showTareasMto,
                    isRequired: checkIfRolAdmin(),
                }),
                order: 4,
            },
            {
                name: 'hora_fin',
                vinculatedFields: undefined,
                label: 'Hora Fin',
                input: {
                    type: InputType.TIME,
                    defaultValue: this.getDefaultHoraFin(),
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({
                    isRememberedOnDuplicate: !this.env.showTareasMto,
                    isRequired: checkIfRolAdmin(),
                }),
                order: 5,
            },
            {
                name: 'horas',
                vinculatedFields: ['hora_inicio', 'hora_fin'],
                label: 'Horas',
                input: { type: InputType.ONLY_TEXT, isEditable: true },
                width: 3,
                computed: {
                    dependsOn: ['hora_inicio', 'hora_fin'],
                    resolve: (context) => this.formatHoursMinutes(
                        this.normalizeTimeValue(context.getValue('hora_inicio')),
                        this.normalizeTimeValue(context.getValue('hora_fin')),
                    ),
                },
                updateValue: () => this.calculateHoras(),
                conditions: this.conditions({ isRememberedOnDuplicate: !this.env.showTareasMto }),
                order: 6,
            },
            {
                name: 'prioridad',
                vinculatedFields: undefined,
                label: 'Prioridad/Realizada',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.prioridades,
                    defaultValue: this.env.showTareasMto && this.user.rol === 'user' ? 'Realizada' : undefined,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                order: 7,
            },
            {
                name: 'mantenimiento',
                vinculatedFields: undefined,
                label: this.isTruthy(this.model.mantenimiento) ? 'Creación' : 'Mantenimiento',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'detorres' }),
                order: 8,
            },
        ];

        return this.visibleSortedFields(fields);
    }

    private getSimpleFields(): FormField<TareaPlantacionModel>[] {
        const fields: FormField<TareaPlantacionModel>[] = [
            {
                name: 'arboles',
                vinculatedFields: undefined,
                label: 'Árboles',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 4,
                conditions: this.conditions(),
                order: 1,
            },
            {
                name: 'arbustos_setos',
                vinculatedFields: undefined,
                label: 'Arbustos/Setos',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 4,
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: 'plantas',
                vinculatedFields: undefined,
                label: 'Plantas',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 4,
                conditions: this.conditions(),
                order: 3,
            },
            {
                name: 'id_cultivo',
                vinculatedFields: undefined,
                label: 'Cultivo',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.cultivos,
                    isEditable: true,
                    dropdownConfiguration: { valuePrimaryKey: 'id' },
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'detorres' }),
                order: 4,
            },
            {
                name: 'id_variedad',
                dependencies: [
                    {
                        source: 'id_cultivo',
                        target: 'id_variedad',
                        kind: 'filter'
                    }
                ],
                vinculatedFields: undefined,
                label: 'Variedad',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.variedades,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it): boolean => this.filterVariedadByCultivo(it.value as VisitasVariedadMesModel | null),
                    },
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'detorres' }),
                order: 5,
            },
            {
                name: 'cantidad',
                vinculatedFields: undefined,
                label: 'Cantidad',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'detorres' }),
                order: 6,
            },
            {
                name: 'unidad_medidad',
                vinculatedFields: undefined,
                label: 'Unidad de Medida',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions({ isVisible: this.env.appName === 'detorres' }),
                order: 7,
            },
        ];

        return this.visibleSortedFields(fields);
    }

    private getWorkerFields(): FormField<TareaPlantacionModel>[] {
        return this.visibleSortedFields([
            {
                name: this.formType < 3 ? 'ids_trabajadores' : 'trabajadores_nombres',
                vinculatedFields: undefined,
                label: 'Profesionales',
                input: {
                    type: InputType.MULTISELECT,
                    data: this.lists.trabajadores,
                    defaultValue: this.getDefaultWorkerId(),
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it): boolean => (it.value as MantenimientoModel | null)?.activo?.toString() === '1',
                    },
                },
                width: 6,
                conditions: this.conditions(),
                order: 1,
            },
        ]);
    }

    private getWorkOrderFields(): FormField<TareaPlantacionModel>[] {
        return this.visibleSortedFields([
            {
                name: 'orden_trabajo',
                vinculatedFields: undefined,
                label: 'Orden de trabajo',
                input: {
                    type: InputType.TEXTAREA,
                    isEditable: checkIfRolAdmin(),
                    textAreaConfiguration: {
                        hasVoiceTranscription: true,
                        canTakePhoto: true,
                        photoField: 'imagen',
                    },
                },
                width: 12,
                conditions: this.conditions(),
                order: 1,
            },
        ]);
    }

    private getObservationFields(): FormField<TareaPlantacionModel>[] {
        return this.visibleSortedFields([
            {
                name: 'observaciones',
                vinculatedFields: undefined,
                label: 'Observaciones',
                input: {
                    type: InputType.TEXTAREA,
                    isEditable: true,
                    textAreaConfiguration: {
                        hasVoiceTranscription: true,
                        canTakePhoto: true,
                        photoField: 'image_after',
                    },
                },
                width: 12,
                conditions: this.conditions(),
                order: 1,
            },
        ]);
    }

    private getHiddenFields(): FormField<TareaPlantacionModel>[] {
        const fields: FormField<TareaPlantacionModel>[] = [
            {
                name: 'id',
                vinculatedFields: undefined,
                label: '',
                input: { type: InputType.HIDDEN, defaultValue: this.model.id, isEditable: false },
                width: 2,
                conditions: this.conditions(),
                order: 1,
            },
            {
                name: 'id_cliente',
                vinculatedFields: ['id_finca'],
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.getInitialClientId(),
                    isEditable: false,
                },
                width: 2,
                updateValue: () => this.getSelectedClientId(),
                conditions: this.conditions({ isVisible: this.env.hasClients && !this.env.canSeeClientes }),
                order: 2,
            },
            {
                name: 'id_usuario',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.formType === FormType.EDITION ? this.model.id_usuario : String(this.user.id ?? ''),
                    isEditable: false,
                },
                width: 2,
                updateValue: () => this.formType === FormType.EDITION ? this.model.id_usuario : String(this.user.id ?? ''),
                conditions: this.conditions(),
                order: 3,
            },
            {
                name: 'imagen',
                vinculatedFields: undefined,
                label: '',
                input: { type: InputType.HIDDEN, defaultValue: this.model.imagen, isEditable: false },
                width: 2,
                conditions: this.conditions(),
                order: 4,
            },
            {
                name: 'image_after',
                vinculatedFields: undefined,
                label: '',
                input: { type: InputType.HIDDEN, defaultValue: this.model.image_after, isEditable: false },
                width: 2,
                conditions: this.conditions(),
                order: 5,
            },
            {
                name: 'signature_img',
                vinculatedFields: undefined,
                label: '',
                input: { type: InputType.HIDDEN, defaultValue: this.model.signature_img, isEditable: false },
                width: 2,
                conditions: this.conditions(),
                order: 6,
            },
            {
                name: 'id_ref_usuario',
                vinculatedFields: undefined,
                label: '',
                input: { type: InputType.HIDDEN, defaultValue: String(this.user.id ?? ''), isEditable: false },
                width: 2,
                conditions: this.conditions({ isVisible: this.env.appName === 'donjardin' && this.formType !== FormType.CREATION }),
                order: 7,
            },
        ];

        return fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getInitialClientId(): string | undefined {
        return this.client?.id ?? this.getSelectedClientId() ?? undefined;
    }

    private getSelectedClientId(): string | undefined {
        const finca = getDropdownSelectedSingle(this.lists.fincas);

        return finca?.id_cliente?.toString() ?? this.client?.id ?? undefined;
    }

    private filterFincaByClient(finca: FincasModel | null): boolean {
        if (!finca) {
            return true;
        }

        if (!this.env.canSeeClientes || this.formType === FormType.DUPLICATION) {
            return true;
        }

        const selectedClient = getDropdownSelectedSingle(this.lists.clientes);

        if (selectedClient) {
            return String(finca.id_cliente ?? '') === String(selectedClient.id ?? '');
        }

        if (this.client) {
            return String(finca.id_cliente ?? '') === String(this.client.id ?? '');
        }

        return true;
    }

    private filterVariedadByCultivo(variedad: VisitasVariedadMesModel | null): boolean {
        if (!variedad) {
            return true;
        }

        return String(variedad.codigo_cultivo ?? '') === String(getDropdownSelectedSingle(this.lists.cultivos)?.codigo ?? '');
    }

    private getDefaultHoraFin(): string | undefined {
        if (!this.env.showTareasMto || this.user.rol !== 'user' || this.formType === FormType.DUPLICATION) {
            return undefined;
        }

        return moment().format('HH:mm');
    }

    private getDefaultWorkerId(): string | undefined {
        if (this.user.rol !== 'user' || this.formType !== FormType.CREATION) {
            return undefined;
        }

        const userId = String(this.user.id ?? '').trim();

        return userId.length > 0 ? userId : undefined;
    }

    private normalizeTimeValue(value: unknown): string {
        if (value instanceof Date) {
            return moment(value).format('HH:mm');
        }

        if (typeof value === 'string') {
            return value.trim();
        }

        return '';
    }

    private getFieldByName(fieldName: string): FormField<TareaPlantacionModel> | null {
        for (const section of this.formFields.sections ?? []) {
            const field = section.fields.find((it) => it.name === fieldName);

            if (field) {
                return field;
            }
        }

        return null;
    }

    private isTruthy(value: unknown): boolean {
        return TRUTHY_VALUES.has(value) || TRUTHY_VALUES.has(String(value ?? ''));
    }

    private visibleSortedFields(fields: FormField<TareaPlantacionModel>[]): FormField<TareaPlantacionModel>[] {
        const visibleFields = fields
            .filter((field) => field.conditions.isVisible)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        this.fieldNames = this.fieldNames.concat(visibleFields);
        this.fieldLabels = this.fieldLabels.concat(visibleFields.map((field) => field.label));

        return visibleFields;
    }

    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }
}
